// SPDX-License-Identifier: GPL-3.0-or-later
// Rules of the SigmaBots table (THEME.md sections 4 to 8).
//
// Pure logic. No clock, no randomness, no I/O. The only time source is `ctx.timeMs`.
// Rules talk to the engine through `RulesContext` and `PhysicsEvent` only.
// Every score goes through the ScoreGate (see scorer.ts), so the legal score gain
// in one second stays below MAX_SCORE_DELTA_PER_SEC.
//
// Assumptions about the engine (see the follow-ups of slice B3):
// - A player input arrives as a PhysicsEvent with ballId 0 (see ITEM in ../ids.ts).
// - `ctx.setMultiplier` only shows the bonus multiplier. The engine does not scale addScore.
// - `ctx.endMode(..., bonus)` is called with bonus 0. The rules pay a mode award with addScore.
// - A ball lock is a counter. The ball in the saucer returns to play after the hold.

import type { LightState, PhysicsEvent, RulesContext, TableRules } from "../../shared/contracts.ts";
import { type FeedbackSink, NULL_SINK } from "../../shared/feedback/index.ts";
import { ASK_COMMAND_KEYS, type RexCommandKey } from "../animation.ts";
import type { ShotId } from "../callouts.ts";
import type { PositionOf } from "../feedback/positions.ts";
import { RuleFeedback } from "../feedback/rule-feedback.ts";
import {
  CALLOUT_TARGET,
  DECORATION,
  HUD_TARGET,
  ITEM,
  LIGHT,
  MODE_ID,
  type ModeId,
  TIMED_MODE_IDS,
} from "../ids.ts";
import { MODES } from "../modes.ts";
import { NULL_RUN } from "../run/director.ts";
import { RunGate } from "../run/run-gate.ts";
import type { BossHitSource, RunRulesPort } from "../run/types.ts";
import {
  BONUS_CAP,
  LIMITS,
  MAX_SCORE_PER_SECOND,
  POINTS,
  SCORE_RATE_CAP_PER_SECOND,
  SCORE_TABLE,
  TIMES,
} from "./constants.ts";

/** Tag of the three drop targets (the `resetTargets` tag). */
export const TOKENS_TAG = "group:tokens";

/** Kick values for the kickback and the saucer. Angle 0 is up. Angle 90 is right. Angle 180 is down. */
const KICKBACK_ANGLE = 0;
const KICKBACK_SPEED = 30;
const KICKBACK_OFF_ANGLE = 180;
const KICKBACK_OFF_SPEED = 6;

/** The jackpot order of SigmaBot Swarm. */
const JACKPOT_ORDER: readonly ShotId[] = ["rampLeft", "rampRight", "orbitLeft", "orbitRight"];

/** The four shots of ASK Mode: both ramps and both orbits. */
const ASK_SHOTS: readonly ShotId[] = ["rampLeft", "rampRight", "orbitLeft", "orbitRight"];

/** The lit top lane, by step number. The order is fixed for every ball. */
const SKILL_LANE_PATTERN: readonly number[] = [0, 1, 2, 1];

/** The one-way gate at the top of the plunger lane. The launched ball always passes it. */
const LANE_GATE = "LaneGate";

const SHOT_LIGHT: Readonly<Record<ShotId, string>> = {
  rampLeft: LIGHT.shotRampLeft,
  rampRight: LIGHT.shotRampRight,
  orbitLeft: LIGHT.shotOrbitLeft,
  orbitRight: LIGHT.shotOrbitRight,
};

const SHOT_REASON: Readonly<Record<ShotId, string>> = {
  rampLeft: "Ghost Ramp",
  rampRight: "Merge Ramp",
  orbitLeft: "Bot Orbit",
  orbitRight: "10X Orbit",
};

const LANE_LIGHTS: readonly string[] = [LIGHT.laneL, LIGHT.laneG, LIGHT.laneT, LIGHT.laneM];
const DENY_LIGHTS: readonly string[] = [LIGHT.denyD, LIGHT.denyE, LIGHT.denyN, LIGHT.denyY];
const TOP_LIGHTS: readonly string[] = [LIGHT.topAccept, LIGHT.topAll, LIGHT.topYes];
const TOP_ITEMS: readonly string[] = [ITEM.topAccept, ITEM.topAll, ITEM.topYes];
const TOKEN_LIGHTS: readonly string[] = [LIGHT.token1, LIGHT.token2, LIGHT.token3];
const MODE_LIGHTS: Readonly<Record<string, string>> = {
  [MODE_ID.acceptAll]: LIGHT.mode1,
  [MODE_ID.worksOnMyMachine]: LIGHT.mode2,
  [MODE_ID.contextWindowFull]: LIGHT.mode3,
  [MODE_ID.yoloFridayDeploy]: LIGHT.mode4,
};
/** One lamp for each lock. `LIMITS.locksForMultiball` is 2, so the row has 2 lamps. */
const LOCK_LIGHTS: readonly string[] = [LIGHT.lock1, LIGHT.lock2];
const BONUS_LIGHTS: readonly string[] = [LIGHT.bonus2, LIGHT.bonus3, LIGHT.bonus4, LIGHT.bonus5];

type SaucerKind = "plain" | "mode_select" | "lock" | "wizard_start" | "super_jackpot" | "grand";

interface SaucerHold {
  readonly kind: SaucerKind;
  readonly until: number;
  /** Index in the list of modes that the player can pick (mode_select only). */
  selection: number;
}

interface ActiveMode {
  readonly id: ModeId;
  readonly startedAt: number;
  readonly endsAt: number;
  readonly totalMs: number;
  completed: boolean;
  shots: number;
  orbits: number;
  tokens: number;
  bumperHits: number;
  /** Bot Orbit shots made before this mode (a mode bonus uses it). */
  readonly earlierWorks: number;
}

interface Multiball {
  readonly startedAt: number;
  jackpotIndex: number;
  jackpots: number;
  superLit: boolean;
}

interface Wizard {
  readonly startedAt: number;
  readonly endsAt: number;
  readonly lit: Set<ShotId>;
  safe: number;
  grandLit: boolean;
}

type EventType = PhysicsEvent["type"];
type EventAction = (ctx: RulesContext, ev: PhysicsEvent) => void;

/** What the rules do for one item: the event type that starts the action, and the action. */
interface ItemRoute {
  readonly type: EventType;
  readonly run: EventAction;
}

/** The lamp state of a flag: off or on. */
const lamp = (on: boolean): LightState => (on ? 1 : 0);

/** The lamp state of a flag for a lamp that can also show the bright state. */
const bright = (on: boolean): LightState => (on ? 2 : 0);

/** The direction of a flipper button: -1 for the left flipper, 1 for the right flipper, else 0. */
const flipperDirection = (item: string): number =>
  item === ITEM.leftFlipper ? -1 : item === ITEM.rightFlipper ? 1 : 0;

/**
 * Options of the rules. Feedback is observe-only: the rules write events to the sink and never
 * read anything back, so a game plays the same with or without a sink.
 */
export interface Vibe01Options {
  /** Where the feedback events go. Unset: nowhere. */
  readonly feedback?: FeedbackSink;
  /** The position of a table element, for the feedback events. Unset: no position. */
  readonly positionOf?: PositionOf;
  /** The run of stages, perks and timed events. Unset: no run. */
  readonly run?: RunRulesPort;
}

/** The rules object. One instance holds the state of one game. */
class Vibe01Rules implements TableRules {
  readonly tableId = "vibe-01";
  readonly modes = MODES;
  readonly scoreTable = SCORE_TABLE;
  readonly maxScorePerSecond = MAX_SCORE_PER_SECOND;

  private readonly fb: RuleFeedback;

  private readonly run: RunRulesPort;
  private readonly gate: RunGate;
  private readonly lightCache = new Map<string, LightState>();
  private readonly animCache = new Map<string, string>();
  private readonly routes: ReadonlyMap<string, ItemRoute> = this.buildRoutes();

  // Game state.
  private modesDone = new Set<ModeId>();
  private swarmPlayed = false;
  private kickbackLit = true;
  private denyLit: boolean[] = [false, false, false, false];
  private denyResetAt = -1;
  private tokensDown: boolean[] = [false, false, false];
  private bankComplete = false;
  private modeStartLit = false;
  private bumperBoost = false;
  private worksOrbitsGame = 0;
  private meter = 0;
  private burnUntil = -1;
  private burnLastMessage = -1;
  private lockCount = 0;
  private lockProgress = 0;
  private lockLit = false;
  private multiball: Multiball | null = null;
  private wizardLit = false;
  private wizard: Wizard | null = null;
  private mode: ActiveMode | null = null;
  private saverUntil = -1;
  private saucer: SaucerHold | null = null;

  // Ball state.
  private shotsMade = 0;
  private bonusMult = 1;
  private lanes: boolean[] = [false, false, false, false];
  private worksOrbitsBall = 0;
  private rightOrbitsBall = 0;
  /** The TOKENS bank and the DENY bank that the player cleared on this ball. */
  private tokensBankBall = false;
  private denyBankBall = false;
  /** True after the target bank combo paid on this ball. It pays once for each ball. */
  private bankComboFired = false;
  private spinnerLitUntil = -1;
  private combo: { shot: ShotId; time: number } | null = null;
  private skillActive = false;
  private skillStart = 0;
  private superUntil = -1;
  private tiltWarnings = 0;
  private tilted = false;
  private lastTimerSecond = -1;

  constructor(options: Vibe01Options = {}) {
    this.fb = new RuleFeedback(
      options.feedback ?? NULL_SINK,
      options.positionOf ?? (() => undefined),
    );
    this.run = options.run ?? NULL_RUN;
    this.gate = new RunGate(SCORE_RATE_CAP_PER_SECOND, this.run);
    this.run.attachPayer(this.gate);
  }

  // ───────────────────────────── TableRules ─────────────────────────────

  onGameStart(ctx: RulesContext): void {
    this.gate.reset();
    this.run.onGameStart(ctx);
    this.lightCache.clear();
    this.animCache.clear();
    this.modesDone = new Set();
    this.swarmPlayed = false;
    this.kickbackLit = true;
    this.denyLit = [false, false, false, false];
    this.denyResetAt = -1;
    this.tokensDown = [false, false, false];
    this.bankComplete = false;
    this.modeStartLit = false;
    this.bumperBoost = false;
    this.worksOrbitsGame = 0;
    this.meter = 0;
    this.burnUntil = -1;
    this.burnLastMessage = -1;
    this.lockCount = 0;
    this.lockProgress = 0;
    this.lockLit = false;
    this.multiball = null;
    this.wizardLit = false;
    this.wizard = null;
    this.mode = null;
    this.saverUntil = -1;
    this.saucer = null;
    this.resetBall(ctx.timeMs);
    ctx.resetTargets(TOKENS_TAG);
    ctx.setMultiplier(this.bonusMult);
    this.sync(ctx);
  }

  onBallStart(ctx: RulesContext): void {
    this.resetBall(ctx.timeMs);
    this.bumperBoost = false;
    if (!this.bankComplete) {
      this.tokensDown = [false, false, false];
      ctx.resetTargets(TOKENS_TAG);
    }
    ctx.setMultiplier(this.bonusMult);
    this.sync(ctx);
  }

  onEvent(ctx: RulesContext, ev: PhysicsEvent): void {
    if (ev.ballId === 0) {
      this.onInput(ctx, ev);
      this.sync(ctx);
      return;
    }
    this.fb.contact(ev);
    if (ev.type === "drain") return;
    if (this.skillActive && !this.isSkillItem(ev.item) && ev.type !== "unhit")
      this.skillActive = false;

    const route = this.routes.get(ev.item);
    if (route && route.type === ev.type) route.run(ctx, ev);
    this.sync(ctx);
  }

  /** The action of each item. An item runs its action for one event type only. */
  private buildRoutes(): ReadonlyMap<string, ItemRoute> {
    const routes = new Map<string, ItemRoute>();
    const on = (items: readonly string[], type: EventType, run: EventAction): void => {
      for (const item of items) routes.set(item, { type, run });
    };
    on([ITEM.shooter], "unhit", (ctx) => this.onLaunch(ctx));
    on([ITEM.topAccept], "hit", (ctx) => this.onTopLane(ctx, 0));
    on([ITEM.topAll], "hit", (ctx) => this.onTopLane(ctx, 1));
    on([ITEM.topYes], "hit", (ctx) => this.onTopLane(ctx, 2));
    on([ITEM.bumper1, ITEM.bumper2, ITEM.bumper3], "hit", (ctx) => this.onBumper(ctx));
    on([ITEM.slingLeft, ITEM.slingRight], "slingshot", (ctx, ev) =>
      this.gate.award(ctx, POINTS.sling, "Slingshot", ev.item),
    );
    on([ITEM.spinner], "spin", (ctx) => this.onSpin(ctx));
    on([ITEM.laneL, ITEM.laneG, ITEM.laneT, ITEM.laneM], "hit", (ctx, ev) =>
      this.onLane(ctx, ev.item),
    );
    on([ITEM.kickback], "hit", (ctx) => this.onKickback(ctx));
    on([ITEM.token1, ITEM.token2, ITEM.token3], "hit", (ctx, ev) => {
      this.onToken(ctx, ev.item);
      this.afterTarget(ctx);
    });
    on([ITEM.denyD, ITEM.denyE, ITEM.denyN, ITEM.denyY], "hit", (ctx, ev) => {
      this.onDeny(ctx, ev.item);
      this.afterTarget(ctx);
    });
    on([ITEM.saucer], "hit", (ctx) => this.onSaucer(ctx));
    on([ITEM.orbitLeftTop], "hit", (ctx, ev) => this.onShot(ctx, "orbitLeft", ev.item));
    on([ITEM.orbitRightTop], "hit", (ctx, ev) => this.onShot(ctx, "orbitRight", ev.item));
    on([ITEM.rampLeftExit], "hit", (ctx, ev) => this.onShot(ctx, "rampLeft", ev.item));
    on([ITEM.rampRightExit], "hit", (ctx, ev) => this.onShot(ctx, "rampRight", ev.item));
    return routes;
  }

  onTick(ctx: RulesContext, dtMs: number): void {
    const now = ctx.timeMs;
    this.gate.flush(ctx);
    this.run.tick(ctx, dtMs);
    this.addPerkBalls(ctx);

    if (this.denyResetAt >= 0 && now >= this.denyResetAt) {
      this.denyLit = [false, false, false, false];
      this.denyResetAt = -1;
    }
    this.tickTokenBurn(ctx);
    this.tickMode(ctx);
    this.tickWizard(ctx);
    this.tickMultiball(ctx);
    this.tickSaucer(ctx);
    this.sync(ctx);
  }

  onBallEnd(ctx: RulesContext): { readonly bonus: number } {
    this.run.onBallEnd();
    // A mode, a multiball and the wizard mode end with the ball. No score is lost.
    if (this.saucer) this.saucer = null;
    if (this.mode) this.finishMode(ctx, false, true);
    if (this.multiball) this.endMultiball(ctx);
    if (this.wizard) this.finishWizard(ctx, false);
    this.combo = null;

    const rawBonus = this.tilted ? 0 : this.shotsMade * POINTS.bonusPerShot * this.bonusMult;
    const shotBonus = Math.min(rawBonus, BONUS_CAP);
    // A count-up that is not paid yet goes into the bonus, up to the cap.
    const queued = this.gate.takePending(BONUS_CAP - shotBonus);
    if (!this.tilted)
      ctx.animate(CALLOUT_TARGET, `bonus:${Math.min(this.shotsMade, 9999)}x${this.bonusMult}`);
    this.sync(ctx);
    return { bonus: shotBonus + queued };
  }

  // ───────────────────────────── state helpers ─────────────────────────────

  private resetBall(now: number): void {
    this.shotsMade = 0;
    this.bonusMult = this.run.bonusMultStart();
    this.lanes = [false, false, false, false];
    this.worksOrbitsBall = 0;
    this.rightOrbitsBall = 0;
    this.tokensBankBall = false;
    this.denyBankBall = false;
    this.bankComboFired = false;
    this.spinnerLitUntil = -1;
    this.combo = null;
    this.skillActive = true;
    this.skillStart = now;
    this.superUntil = -1;
    this.tiltWarnings = 0;
    this.tilted = false;
    this.saucer = null;
    this.lastTimerSecond = -1;
  }

  private isSkillItem(item: string): boolean {
    return (
      item === ITEM.shooter ||
      item === LANE_GATE ||
      item === ITEM.topAccept ||
      item === ITEM.topAll ||
      item === ITEM.topYes
    );
  }

  private callout(ctx: RulesContext, id: string): void {
    ctx.animate(CALLOUT_TARGET, id);
  }

  private save(ctx: RulesContext, ms: number): void {
    ctx.ballSave(ms);
    this.saverUntil = Math.max(this.saverUntil, ctx.timeMs + ms);
  }

  private uncompletedModes(): ModeId[] {
    return TIMED_MODE_IDS.filter((id) => !this.modesDone.has(id));
  }

  private modeStartAvailable(): boolean {
    return this.bankComplete && this.uncompletedModes().length > 0;
  }

  // ───────────────────────────── inputs ─────────────────────────────

  private onInput(ctx: RulesContext, ev: PhysicsEvent): void {
    if (ev.item === ITEM.tilt) {
      this.onTilt(ctx);
      return;
    }
    const dir = ev.kind === "flipper" ? flipperDirection(ev.item) : 0;
    if (dir === 0) return;
    if (this.saucer && this.saucer.kind === "mode_select") {
      this.moveModeSelection(ctx, this.saucer, dir);
      return;
    }
    this.shiftLanes(dir);
  }

  /** The flipper buttons choose the mode while the saucer holds the ball. */
  private moveModeSelection(ctx: RulesContext, saucer: SaucerHold, dir: number): void {
    const list = this.uncompletedModes();
    if (list.length === 0) return;
    saucer.selection = (saucer.selection + dir + list.length) % list.length;
    const picked = list[saucer.selection];
    if (picked) this.callout(ctx, `select:${picked}`);
  }

  /** Lane change: move the lit letters one lane left or right. */
  private shiftLanes(dir: number): void {
    const n = this.lanes.length;
    const next: boolean[] = [];
    for (let i = 0; i < n; i++) next[(i + dir + n) % n] = this.lanes[i] === true;
    this.lanes = next;
  }

  private onTilt(ctx: RulesContext): void {
    if (this.tilted) return;
    this.tiltWarnings++;
    if (this.tiltWarnings <= LIMITS.tiltWarnings) {
      this.fb.tiltWarning(this.tiltWarnings);
      this.callout(ctx, `tilt_warning:${this.tiltWarnings}`);
      return;
    }
    // The third tilt event: the bonus is cancelled for this ball. The score stays.
    // The mode ends first. The last call ends the ball (the engine drains every ball).
    this.tilted = true;
    this.fb.tilt();
    this.callout(ctx, "tilt");
    if (this.mode) this.finishMode(ctx, false, true);
    if (this.saucer) this.saucer = null;
    ctx.endBall("tilt");
  }

  // ───────────────────────────── launch and skill shot ─────────────────────────────

  private onLaunch(ctx: RulesContext): void {
    const yolo = this.mode?.id === MODE_ID.yoloFridayDeploy;
    if (!this.multiball && !this.wizard && !yolo)
      this.save(ctx, this.run.launchSaveMs(TIMES.ballSaveLaunchMs));
  }

  private skillLane(now: number): number {
    const step = Math.floor(Math.max(0, now - this.skillStart) / TIMES.skillLaneStepMs);
    return SKILL_LANE_PATTERN[step % SKILL_LANE_PATTERN.length] ?? 0;
  }

  private onTopLane(ctx: RulesContext, lane: number): void {
    if (!this.skillActive) return;
    this.skillActive = false;
    if (lane === this.skillLane(ctx.timeMs)) {
      this.gate.award(ctx, POINTS.skillShot, "Skill shot", TOP_LIGHTS[lane]);
      this.fb.skillShot(TOP_ITEMS[lane] ?? ITEM.topAll, "Skill shot", POINTS.skillShot);
      this.callout(ctx, "skill_shot");
      this.superUntil = ctx.timeMs + TIMES.superSkillMs;
    } else {
      this.gate.award(ctx, POINTS.skillOther, "Top lane", TOP_LIGHTS[lane]);
    }
  }

  // ───────────────────────────── simple scoring items ─────────────────────────────

  private onBumper(ctx: RulesContext): void {
    const now = ctx.timeMs;
    if (this.burnUntil >= 0) return; // Rate-Limit Randy holds the bumpers.
    this.run.signal(ctx, "bumper");
    this.gate.award(
      ctx,
      this.bumperBoost ? POINTS.bumperBoost : POINTS.bumper,
      "Token Burn bumper",
    );
    this.meter++;
    if (this.mode?.id === MODE_ID.contextWindowFull) {
      this.mode.bumperHits++;
      this.checkContextBar(ctx);
    }
    if (this.meter >= LIMITS.tokenBurnMeter) {
      this.meter = 0;
      this.gate.award(ctx, POINTS.meterFull, "Token Burn meter full");
      this.callout(ctx, "token_burn_full");
      this.callout(ctx, "rate_limited");
      this.burnUntil = now + TIMES.tokenBurnPauseMs;
      this.burnLastMessage = now;
      ctx.animate(DECORATION.randy, `wait:${(TIMES.tokenBurnPauseMs / 1000).toFixed(2)}`);
    }
    this.hitBoss(ctx, "bumper");
  }

  private onSpin(ctx: RulesContext): void {
    const lit = ctx.timeMs < this.spinnerLitUntil;
    let points = lit ? POINTS.spinLit : POINTS.spin;
    if (this.mode?.id === MODE_ID.yoloFridayDeploy) points *= 2;
    this.gate.award(ctx, points, lit ? "Bot Spinner (lit)" : "Bot Spinner", ITEM.spinner);
  }

  private onLane(ctx: RulesContext, item: string): void {
    const index = item === ITEM.laneL ? 0 : item === ITEM.laneG ? 1 : item === ITEM.laneT ? 2 : 3;
    const outlane = index === 0 || index === 3;
    this.gate.award(
      ctx,
      outlane ? POINTS.outlane : POINTS.inlane,
      outlane ? "Outlane" : "Inlane",
      item,
    );
    this.lanes[index] = true;
    if (this.lanes.every(Boolean)) {
      this.lanes = [false, false, false, false];
      if (this.bonusMult < LIMITS.bonusMultiplierMax) {
        this.bonusMult++;
        ctx.setMultiplier(this.bonusMult);
        this.callout(ctx, "lgtm");
      }
    }
  }

  private onKickback(ctx: RulesContext): void {
    if (this.kickbackLit) {
      this.kickbackLit = false;
      ctx.kick(ITEM.kickback, KICKBACK_ANGLE, KICKBACK_SPEED);
      ctx.animate(DECORATION.dennis, "kickback");
      this.callout(ctx, "kickback_denied");
    } else {
      // No guard here: the ball goes down to the drain.
      ctx.kick(ITEM.kickback, KICKBACK_OFF_ANGLE, KICKBACK_OFF_SPEED);
    }
  }

  private onToken(ctx: RulesContext, item: string): void {
    const index = item === ITEM.token1 ? 0 : item === ITEM.token2 ? 1 : 2;
    this.shotsMade++;
    this.gate.award(ctx, POINTS.tokenTarget, "TOKENS target", item);
    this.tokensDown[index] = true;
    if (this.mode?.id === MODE_ID.contextWindowFull) {
      this.mode.tokens++;
      this.gate.award(ctx, POINTS.contextTarget, "Context Window Full: target", item);
    }
    if (!this.tokensDown.every(Boolean)) return;

    // The Context Window bank is complete.
    this.gate.award(ctx, POINTS.tokenBank, "Context Window bank");
    this.fb.bankComplete("Context Window bank", POINTS.tokenBank, DECORATION.carl);
    this.run.signal(ctx, "bank");
    this.tokensBankBall = true;
    this.bankComplete = true;
    this.bumperBoost = true;
    if (this.mode?.id === MODE_ID.contextWindowFull) {
      this.gate.award(ctx, POINTS.contextComplete, "Context Window Full: complete");
      this.finishMode(ctx, true);
    }
    if (this.modeStartAvailable()) {
      this.modeStartLit = true;
      this.callout(ctx, "mode_start_lit");
    } else {
      // No mode to start: raise the targets again so they keep scoring.
      this.bankComplete = false;
      this.tokensDown = [false, false, false];
      ctx.resetTargets(TOKENS_TAG);
    }
  }

  private onDeny(ctx: RulesContext, item: string): void {
    const index = item === ITEM.denyD ? 0 : item === ITEM.denyE ? 1 : item === ITEM.denyN ? 2 : 3;
    this.shotsMade++;
    this.gate.award(ctx, POINTS.denyTarget, "DENY target", item);
    if (this.denyResetAt < 0) {
      this.denyLit[index] = true;
      if (this.denyLit.every(Boolean)) {
        this.gate.award(ctx, POINTS.denyBank, "DENY bank");
        this.fb.bankComplete("DENY bank", POINTS.denyBank, DECORATION.dennis);
        this.denyBankBall = true;
        this.callout(ctx, "deny_not_today");
        this.callout(ctx, "kickback_lit");
        this.kickbackLit = true;
        this.save(ctx, TIMES.ballSaveBankMs);
        this.denyResetAt = ctx.timeMs + TIMES.denyResetMs;
      }
    }
    const m = this.mode;
    if (m && m.id === MODE_ID.acceptAll) {
      // A DENY target stops the 2x and pays a flat bonus for each second that is left.
      const secondsLeft = Math.max(0, Math.floor((m.endsAt - ctx.timeMs) / 1000));
      this.gate.award(ctx, secondsLeft * POINTS.acceptAllFlatPerSecond, "Accept All: flat bonus");
      if (!m.completed) {
        m.completed = true;
        this.gate.award(ctx, POINTS.acceptAllComplete, "Accept All: complete");
      }
      this.finishMode(ctx, true);
    }
  }

  // ───────────────────────────── shots (orbits and ramps) ─────────────────────────────

  private baseShotPoints(shot: ShotId): number {
    switch (shot) {
      case "orbitLeft":
        return POINTS.orbitLeft;
      case "orbitRight":
        return POINTS.orbitRight;
      default:
        return POINTS.ramp;
    }
  }

  private onShot(ctx: RulesContext, shot: ShotId, item: string): void {
    const now = ctx.timeMs;
    this.shotsMade++;
    const isOrbit = shot === "orbitLeft" || shot === "orbitRight";
    const mode = this.mode;

    // 1. The score of the shot. One rule decides. The rules never stack by chance.
    const { points, reason } = this.scoreShot(ctx, shot, isOrbit);
    this.gate.award(ctx, points, reason, item);

    // 2. Combo. 3. Counters and callouts of the shot. 4. Mode progress.
    this.scoreCombo(ctx, shot, now);
    this.countShot(ctx, shot, now);
    this.run.onShot(ctx, shot);
    if (mode && this.mode === mode) this.onModeShot(ctx, mode, isOrbit);
    this.hitBoss(ctx, isOrbit ? "orbit" : "ramp");
  }

  /** The score of a shot and its reason. A special state of the table decides first. */
  private scoreShot(
    ctx: RulesContext,
    shot: ShotId,
    isOrbit: boolean,
  ): { readonly points: number; readonly reason: string } {
    if (this.wizard?.lit.has(shot)) {
      this.onWizardShot(ctx, shot);
      return { points: POINTS.askDeny, reason: "ASK Mode: DENY" };
    }
    if (this.multiball && JACKPOT_ORDER[this.multiball.jackpotIndex] === shot) {
      this.onJackpot(ctx, shot);
      return { points: POINTS.jackpot, reason: "SigmaBot Swarm: jackpot" };
    }
    if (shot === "rampRight" && ctx.timeMs < this.superUntil) {
      this.superUntil = -1;
      this.fb.skillShot(ITEM.rampRightExit, "Super skill shot", POINTS.superSkill);
      this.callout(ctx, "super_skill");
      return { points: POINTS.superSkill, reason: "Super skill shot" };
    }
    return { points: this.modeShotPoints(shot, isOrbit), reason: SHOT_REASON[shot] };
  }

  /** The score of a shot with no special state. The active timed mode can change it. */
  private modeShotPoints(shot: ShotId, isOrbit: boolean): number {
    const id = this.mode?.id;
    if (id === MODE_ID.worksOnMyMachine && isOrbit) return POINTS.worksOrbit;
    const base = this.baseShotPoints(shot);
    if (id === MODE_ID.acceptAll) return base * 2;
    if (id === MODE_ID.yoloFridayDeploy && !isOrbit) return base * 2;
    return base;
  }

  /** Combo. Two shots of the set in 4.0 s. Ramp to ramp is Merged Ramps. */
  private scoreCombo(ctx: RulesContext, shot: ShotId, now: number): void {
    const prev = this.combo;
    if (prev && now - prev.time < this.run.comboWindowMs(TIMES.comboWindowMs)) {
      const rampPair =
        prev.shot !== shot && prev.shot.startsWith("ramp") && shot.startsWith("ramp");
      if (rampPair) {
        this.gate.award(ctx, POINTS.merged, "Merged Ramps");
        this.fb.combo("Merged Ramps", POINTS.merged);
        this.callout(ctx, "merged_no_review");
      } else {
        this.gate.award(ctx, POINTS.combo, "Combo");
        this.fb.combo("Combo", POINTS.combo);
        this.callout(ctx, "combo");
      }
      this.run.signal(ctx, "combo");
    }
    this.combo = { shot, time: now };
  }

  /** The counters and the callouts of a shot. */
  private countShot(ctx: RulesContext, shot: ShotId, now: number): void {
    if (shot === "orbitLeft") {
      this.worksOrbitsGame++;
      this.worksOrbitsBall++;
      if (this.worksOrbitsBall % LIMITS.worksOrbitsInBallForSpinner === 0) {
        this.spinnerLitUntil = now + TIMES.spinnerLitMs;
      }
      this.callout(ctx, "works_on_my_machine");
    } else if (shot === "orbitRight") {
      this.rightOrbitsBall++;
      this.callout(ctx, "ten_x_engineer");
    } else if (shot === "rampLeft") {
      ctx.animate(DECORATION.haley, "shot");
      this.callout(ctx, "target_missing");
      this.countLockShot(ctx);
    }
  }

  private countLockShot(ctx: RulesContext): void {
    if (this.multiball || this.wizard || this.lockLit || this.lockCount >= LIMITS.locksForMultiball)
      return;
    this.lockProgress++;
    const need = this.lockCount === 0 ? LIMITS.rampShotsFirstLock : LIMITS.rampShotsNextLock;
    if (this.lockProgress >= need) {
      this.lockProgress = 0;
      this.lockLit = true;
      this.callout(ctx, "lock_lit");
    }
  }

  private onModeShot(ctx: RulesContext, m: ActiveMode, isOrbit: boolean): void {
    if (m.id === MODE_ID.acceptAll) {
      m.shots++;
      if (m.shots >= LIMITS.acceptShotsToComplete && !m.completed) {
        m.completed = true;
        this.gate.award(ctx, POINTS.acceptAllComplete, "Accept All: complete");
        this.callout(ctx, "mode_done:accept_all");
      }
    } else if (m.id === MODE_ID.worksOnMyMachine && isOrbit) {
      m.orbits++;
      if (m.orbits >= LIMITS.worksOrbitsToComplete) {
        m.completed = true;
        this.gate.award(
          ctx,
          POINTS.worksComplete + POINTS.worksPerEarlierOrbit * m.earlierWorks,
          "Bot Orbit Run: complete",
        );
        this.finishMode(ctx, true);
      }
    }
  }

  // ───────────────────────────── multiball ─────────────────────────────

  private onJackpot(ctx: RulesContext, shot: ShotId): void {
    const mb = this.multiball;
    if (!mb) return;
    mb.jackpots++;
    mb.jackpotIndex++;
    this.fb.jackpot(shot, POINTS.jackpot);
    this.run.signal(ctx, "jackpot");
    this.callout(ctx, "jackpot_approved");
    if (mb.jackpotIndex >= LIMITS.jackpotsForSuper) {
      mb.superLit = true;
      this.callout(ctx, "super_jackpot_lit");
    }
  }

  /**
   * Add up to `count` balls to play. The cap is `LIMITS.maxLiveBalls` for every path.
   * Returns the number of balls added.
   */
  private addLiveBalls(ctx: RulesContext, count: number): number {
    const room = Math.max(0, LIMITS.maxLiveBalls - ctx.ballsInPlay);
    const added = Math.min(count, room);
    for (let i = 0; i < added; i++) ctx.addBall(ITEM.plunger);
    return added;
  }

  /** Add the balls of a picked perk, as many as fit under the cap. */
  private addPerkBalls(ctx: RulesContext): void {
    const room = Math.max(0, LIMITS.maxLiveBalls - ctx.ballsInPlay);
    const count = this.run.takeBonusBalls(room);
    if (count > 0) this.addLiveBalls(ctx, count);
  }

  /**
   * A multiball reward (a boss defeat or the target bank combo). With no multiball it starts
   * SigmaBot Swarm with `balls` added balls. While SigmaBot Swarm or ASK MODE runs, it adds the balls
   * to play up to the cap and extends the ball save.
   */
  private rewardMultiball(ctx: RulesContext, balls: number): void {
    if (!this.multiball && !this.wizard) {
      this.startMultiball(ctx, balls);
      return;
    }
    if (this.addLiveBalls(ctx, balls) > 0) this.save(ctx, TIMES.ballSaveSwarmMs);
  }

  /** Both target banks (TOKENS and DENY) are clear on this ball: pay the 2-ball multiball once. */
  private checkBankCombo(ctx: RulesContext): void {
    if (this.bankComboFired || !this.tokensBankBall || !this.denyBankBall) return;
    this.bankComboFired = true;
    this.rewardMultiball(ctx, LIMITS.bankComboBalls);
  }

  /** A ball hit a boss source. A beaten boss pays the victory multiball. */
  private hitBoss(ctx: RulesContext, source: BossHitSource): void {
    if (this.run.bossHit(ctx, source)) this.rewardMultiball(ctx, LIMITS.victoryMultiballBalls);
  }

  /** The work after a target hit: the bank combo first, then the boss damage. */
  private afterTarget(ctx: RulesContext): void {
    this.checkBankCombo(ctx);
    this.hitBoss(ctx, "target");
  }

  private startMultiball(ctx: RulesContext, balls: number): void {
    if (this.mode) this.finishMode(ctx, false, true);
    this.multiball = { startedAt: ctx.timeMs, jackpotIndex: 0, jackpots: 0, superLit: false };
    this.swarmPlayed = true;
    this.lockCount = 0;
    this.lockProgress = 0;
    this.lockLit = false;
    ctx.startMode(MODE_ID.agentSwarm);
    this.addLiveBalls(ctx, balls);
    this.fb.multiballStart(ctx.ballsInPlay);
    this.save(ctx, TIMES.ballSaveSwarmMs);
    this.callout(ctx, "agent_swarm");
    this.run.signal(ctx, "multiball");
  }

  private endMultiball(ctx: RulesContext): void {
    const mb = this.multiball;
    if (!mb) return;
    this.multiball = null;
    ctx.endMode(MODE_ID.agentSwarm, mb.jackpots > 0, 0);
    this.checkWizardReady(ctx);
  }

  private tickMultiball(ctx: RulesContext): void {
    const mb = this.multiball;
    if (!mb) return;
    if (ctx.timeMs - mb.startedAt >= TIMES.multiballGraceMs && ctx.ballsInPlay <= 1)
      this.endMultiball(ctx);
  }

  // ───────────────────────────── wizard mode ─────────────────────────────

  private checkWizardReady(ctx: RulesContext): void {
    if (this.wizardLit || this.wizard) return;
    const modesAllDone = TIMED_MODE_IDS.every((id) => this.modesDone.has(id));
    if (modesAllDone && this.swarmPlayed) {
      this.wizardLit = true;
      this.callout(ctx, "wizard_lit");
    }
  }

  private startWizard(ctx: RulesContext): void {
    if (this.mode) this.finishMode(ctx, false, true);
    this.wizardLit = false;
    this.wizard = {
      startedAt: ctx.timeMs,
      endsAt: ctx.timeMs + TIMES.wizard,
      lit: new Set(ASK_SHOTS),
      safe: 0,
      grandLit: false,
    };
    ctx.startMode(MODE_ID.askMode);
    ctx.addBall(ITEM.plunger);
    this.save(ctx, TIMES.wizard);
    this.callout(ctx, "ask_mode");
  }

  private onWizardShot(ctx: RulesContext, shot: ShotId): void {
    const w = this.wizard;
    if (!w) return;
    w.lit.delete(shot);
    w.safe++;
    if (w.safe >= LIMITS.askDenyTotal) {
      w.grandLit = true;
      this.callout(ctx, "grand_lit");
    } else if (w.lit.size === 0) {
      for (const s of ASK_SHOTS) w.lit.add(s);
    }
  }

  private finishWizard(ctx: RulesContext, completed: boolean): void {
    if (!this.wizard) return;
    this.wizard = null;
    ctx.endMode(MODE_ID.askMode, completed, 0);
    // The mode lamps reset. The player can play all modes and the wizard mode again.
    this.modesDone = new Set();
    this.swarmPlayed = false;
    this.wizardLit = false;
    this.run.signal(ctx, "mode_finished");
  }

  private tickWizard(ctx: RulesContext): void {
    const w = this.wizard;
    if (w && ctx.timeMs >= w.endsAt) this.finishWizard(ctx, w.grandLit);
  }

  // ───────────────────────────── timed modes ─────────────────────────────

  private startTimedMode(ctx: RulesContext, id: ModeId): void {
    const now = ctx.timeMs;
    const baseMs =
      id === MODE_ID.acceptAll
        ? TIMES.accept
        : id === MODE_ID.worksOnMyMachine
          ? TIMES.works
          : id === MODE_ID.contextWindowFull
            ? TIMES.context
            : TIMES.yolo;
    const totalMs = this.run.modeMs(baseMs);
    this.mode = {
      id,
      startedAt: now,
      endsAt: now + totalMs,
      totalMs,
      completed: false,
      shots: 0,
      orbits: 0,
      tokens: 0,
      bumperHits: 0,
      earlierWorks: this.worksOrbitsGame,
    };
    this.gate.resetRequested();
    this.lastTimerSecond = -1;
    this.modeStartLit = false;
    // The saucer started the mode: raise the Context Window bank again.
    this.bankComplete = false;
    this.tokensDown = [false, false, false];
    ctx.resetTargets(TOKENS_TAG);
    ctx.startMode(id);
    ctx.animate(DECORATION.rex, "aim:center");
    switch (id) {
      case MODE_ID.acceptAll:
        this.callout(ctx, "accept_ask_first");
        break;
      case MODE_ID.worksOnMyMachine:
        this.callout(ctx, "works_on_my_machine");
        break;
      case MODE_ID.contextWindowFull:
        this.callout(ctx, "context_window_full");
        break;
      default:
        this.callout(ctx, "friday_deploy_started");
        this.save(ctx, totalMs);
        break;
    }
  }

  private finishMode(ctx: RulesContext, completed: boolean, quiet = false): void {
    const m = this.mode;
    if (!m) return;
    this.mode = null;
    ctx.endMode(m.id, completed, 0);
    ctx.animate(HUD_TARGET, "timer:0:0");
    this.lastTimerSecond = -1;
    if (completed) {
      this.modesDone.add(m.id);
      this.run.signal(ctx, "mode_finished");
      if (m.id === MODE_ID.yoloFridayDeploy) this.callout(ctx, "deployed");
      else if (m.id !== MODE_ID.acceptAll) this.callout(ctx, `mode_done:${m.id}`);
      this.checkWizardReady(ctx);
    } else if (quiet) {
      // The mode ended with the ball or with a multiball. No failure line.
    } else if (m.id === MODE_ID.yoloFridayDeploy) {
      this.callout(ctx, "rolled_back");
    } else if (m.id === MODE_ID.contextWindowFull) {
      this.callout(ctx, "context_over");
    }
  }

  private contextBarMs(m: ActiveMode, now: number): number {
    return now - m.startedAt + m.bumperHits * 1000;
  }

  private checkContextBar(ctx: RulesContext): void {
    const m = this.mode;
    if (!m || m.id !== MODE_ID.contextWindowFull) return;
    if (this.contextBarMs(m, ctx.timeMs) >= m.totalMs) this.finishMode(ctx, false);
  }

  private tickMode(ctx: RulesContext): void {
    const m = this.mode;
    if (!m) return;
    const now = ctx.timeMs;
    if (m.id === MODE_ID.yoloFridayDeploy && this.gate.requestedTotal >= POINTS.yoloGoal) {
      this.gate.award(ctx, POINTS.yoloComplete, "Bot Friday Deploy: complete");
      m.completed = true;
      this.finishMode(ctx, true);
      return;
    }
    if (m.id === MODE_ID.contextWindowFull) {
      this.checkContextBar(ctx);
      if (this.mode !== m) return;
    } else if (now >= m.endsAt) {
      this.finishMode(ctx, m.completed);
      return;
    }
    const usedMs =
      m.id === MODE_ID.contextWindowFull ? this.contextBarMs(m, now) : now - m.startedAt;
    const secondsLeft = Math.max(0, Math.ceil((m.totalMs - usedMs) / 1000));
    if (secondsLeft !== this.lastTimerSecond) {
      this.lastTimerSecond = secondsLeft;
      ctx.animate(HUD_TARGET, `timer:${secondsLeft}:${Math.round(m.totalMs / 1000)}`);
      if (m.id === MODE_ID.yoloFridayDeploy) {
        const elapsed = Math.min(40, Math.round(m.totalMs / 1000) - secondsLeft);
        ctx.animate(CALLOUT_TARGET, `clock:${elapsed}`);
      }
    }
  }

  // ───────────────────────────── Token Burn ─────────────────────────────

  private tickTokenBurn(ctx: RulesContext): void {
    if (this.burnUntil < 0) return;
    const now = ctx.timeMs;
    const left = this.burnUntil - now;
    if (left <= 0) {
      this.burnUntil = -1;
      ctx.animate(DECORATION.randy, "wait:0");
      return;
    }
    if (now - this.burnLastMessage >= TIMES.randyStepMs) {
      this.burnLastMessage = now;
      ctx.animate(DECORATION.randy, `wait:${(left / 1000).toFixed(2)}`);
    }
  }

  // ───────────────────────────── saucer ─────────────────────────────

  private onSaucer(ctx: RulesContext): void {
    if (this.saucer) return;
    this.shotsMade++;
    const until = ctx.timeMs + TIMES.saucerHoldMs;
    const special = this.specialSaucer(ctx, until);
    if (special) {
      this.saucer = special;
      return;
    }
    this.gate.award(ctx, POINTS.saucer, "Prod Bot saucer shot", ITEM.saucer);
    this.saucer = this.regularSaucer(ctx, until);
  }

  /** A saucer hold for the wizard mode or a jackpot. It pays its own score. `null` for a plain saucer. */
  private specialSaucer(ctx: RulesContext, until: number): SaucerHold | null {
    if (this.wizardLit && !this.wizard && !this.mode && !this.multiball) {
      this.gate.award(ctx, POINTS.saucer, "Prod Bot saucer shot", ITEM.saucer);
      return { kind: "wizard_start", until, selection: 0 };
    }
    if (this.wizard?.grandLit) {
      this.gate.award(ctx, POINTS.grandApproval, "ASK Mode: Grand Approval", ITEM.saucer);
      this.callout(ctx, "deleted_nothing");
      this.finishWizard(ctx, true);
      return { kind: "grand", until, selection: 0 };
    }
    if (this.multiball?.superLit) {
      this.gate.award(ctx, POINTS.superJackpot, "SigmaBot Swarm: super jackpot", ITEM.saucer);
      this.fb.jackpot(null, POINTS.superJackpot);
      this.callout(ctx, "jackpot_approved");
      this.multiball.superLit = false;
      this.multiball.jackpotIndex = 0;
      return { kind: "super_jackpot", until, selection: 0 };
    }
    return null;
  }

  /** The saucer hold after the plain saucer score: a ball lock, the mode choice, or a plain hold. */
  private regularSaucer(ctx: RulesContext, until: number): SaucerHold {
    if (this.lockLit && this.noFeatureMode()) {
      this.lockLit = false;
      this.lockCount++;
      this.fb.ballLock(this.lockCount);
      this.callout(ctx, `lock:${this.lockCount}`);
      return { kind: "lock", until, selection: 0 };
    }
    if (this.modeStartLit && !this.mode && this.noFeatureMode()) {
      const first = this.uncompletedModes()[0];
      if (first) {
        this.callout(ctx, `select:${first}`);
        return {
          kind: "mode_select",
          until: ctx.timeMs + TIMES.saucerModeSelectMs,
          selection: 0,
        };
      }
    }
    return { kind: "plain", until, selection: 0 };
  }

  /** True when no multiball and no wizard mode runs. */
  private noFeatureMode(): boolean {
    return !this.multiball && !this.wizard;
  }

  private tickSaucer(ctx: RulesContext): void {
    const s = this.saucer;
    if (!s || ctx.timeMs < s.until) return;
    this.saucer = null;
    switch (s.kind) {
      case "mode_select": {
        const list = this.uncompletedModes();
        const picked = list[s.selection] ?? list[0];
        if (picked) this.startTimedMode(ctx, picked);
        break;
      }
      case "wizard_start":
        this.startWizard(ctx);
        break;
      case "lock":
        if (this.lockCount >= LIMITS.locksForMultiball)
          this.startMultiball(ctx, LIMITS.lockMultiballBalls);
        break;
      default:
        break;
    }
    ctx.kick(ITEM.saucer);
  }

  // ───────────────────────────── run hooks (optional engine seam, see rules-ext.ts) ─────────────

  holdActive(): boolean {
    return this.run.holdActive();
  }

  onHoldTick(dtMs: number): void {
    this.run.onHoldTick(dtMs);
  }

  extraBallsEarned(): number {
    return this.run.extraBallsEarned();
  }

  // ───────────────────────────── lights and animations ─────────────────────────────

  private shotLamp(shot: ShotId, now: number): LightState {
    if (this.wizard) return this.wizard.lit.has(shot) ? 2 : 0;
    if (this.multiball) return JACKPOT_ORDER[this.multiball.jackpotIndex] === shot ? 2 : 0;
    const modeLamp = this.modeShotLamp(shot);
    if (modeLamp !== null) return modeLamp;
    return bright(shot === "rampRight" && now < this.superUntil);
  }

  /** The lamp of a shot while a timed mode runs. `null` when no timed mode lights the shots. */
  private modeShotLamp(shot: ShotId): LightState | null {
    switch (this.mode?.id) {
      case MODE_ID.acceptAll:
        return 1;
      case MODE_ID.worksOnMyMachine:
        return lamp(shot.startsWith("orbit"));
      case MODE_ID.yoloFridayDeploy:
        return lamp(shot.startsWith("ramp"));
      default:
        return null;
    }
  }

  private rexCommand(): RexCommandKey {
    if (this.wizard) return ASK_COMMAND_KEYS[this.wizard.safe] ?? "all_denied";
    if (this.multiball) return "spawn_agents";
    switch (this.mode?.id) {
      case MODE_ID.acceptAll:
        return "accept_all";
      case MODE_ID.worksOnMyMachine:
        return "ship_it";
      case MODE_ID.contextWindowFull:
        return "paste_repo";
      case MODE_ID.yoloFridayDeploy:
        return "deploy_friday";
      default:
        return "standby";
    }
  }

  private goalLine(): string {
    const m = this.mode;
    if (this.wizard)
      return this.wizard.grandLit
        ? "goal:grand_approval"
        : `goal:deny_shots:${LIMITS.askDenyTotal - this.wizard.safe}`;
    if (this.multiball) return this.multiball.superLit ? "goal:super_jackpot" : "goal:lit_jackpot";
    if (!m) return "goal:none";
    switch (m.id) {
      case MODE_ID.acceptAll:
        return m.completed
          ? "goal:none"
          : `goal:make_shots:${LIMITS.acceptShotsToComplete - m.shots}`;
      case MODE_ID.worksOnMyMachine:
        return `goal:make_orbits:${LIMITS.worksOrbitsToComplete - m.orbits}`;
      case MODE_ID.contextWindowFull:
        return `goal:drop_tokens:${Math.max(0, 3 - m.tokens)}`;
      default:
        return `goal:score_points:${Math.max(0, POINTS.yoloGoal - this.gate.requestedTotal)}`;
    }
  }

  private setLamp(ctx: RulesContext, name: string, state: LightState): void {
    if (this.lightCache.get(name) === state) return;
    this.lightCache.set(name, state);
    ctx.setLight(name, state);
  }

  private setAnim(ctx: RulesContext, target: string, animation: string): void {
    if (this.animCache.get(target) === animation) return;
    this.animCache.set(target, animation);
    ctx.animate(target, animation);
  }

  /** Send every light and every state-based animation that changed. */
  private sync(ctx: RulesContext): void {
    const now = ctx.timeMs;
    this.syncBankLamps(ctx);
    this.syncTopLamps(ctx, now);
    this.syncSaucerLamps(ctx);
    this.syncProgressLamps(ctx, now);
    this.syncAnimations(ctx);
  }

  /** Send a row of lamps. A lamp is on when `isOn` is true for its index. */
  private setRow(ctx: RulesContext, names: readonly string[], isOn: (i: number) => boolean): void {
    names.forEach((name, i) => {
      this.setLamp(ctx, name, lamp(isOn(i)));
    });
  }

  /** The lamps of the lanes, the DENY letters, the token targets and the kickback. */
  private syncBankLamps(ctx: RulesContext): void {
    this.setRow(ctx, LANE_LIGHTS, (i) => this.lanes[i] === true);
    this.setRow(ctx, DENY_LIGHTS, (i) => this.denyLit[i] === true);
    this.setRow(ctx, TOKEN_LIGHTS, (i) => this.tokensDown[i] === true);
    this.setLamp(ctx, LIGHT.kickback, lamp(this.kickbackLit));
    this.setLamp(ctx, LIGHT.noGuard, 1);
  }

  /** The three top lane lamps. Only the lit skill lane shows while the skill shot is open. */
  private syncTopLamps(ctx: RulesContext, now: number): void {
    const skillLane = this.skillActive ? this.skillLane(now) : -1;
    this.setRow(ctx, TOP_LIGHTS, (i) => i === skillLane);
  }

  /** The lamp of the mode choice at the saucer. Off when no mode start is lit. */
  private saucerModeLamp(): LightState {
    if (!this.modeStartLit) return 0;
    return this.mode || this.multiball || this.wizard ? 1 : 2;
  }

  /** The lamps of the saucer. */
  private syncSaucerLamps(ctx: RulesContext): void {
    const grand = this.wizard?.grandLit === true;
    const superLit = this.multiball?.superLit === true;
    const lockReady = this.lockLit && this.noFeatureMode();
    this.setLamp(ctx, LIGHT.saucerWizard, bright(this.wizardLit));
    this.setLamp(ctx, LIGHT.saucerLock, bright(lockReady));
    this.setLamp(ctx, LIGHT.saucerMode, this.saucerModeLamp());
    this.setLamp(ctx, LIGHT.saucerJackpot, bright(grand || superLit));
    this.setLamp(
      ctx,
      LIGHT.saucerArrow,
      lamp(this.wizardLit || lockReady || this.modeStartLit || grand || superLit),
    );
  }

  /** The lamps of the shots, the spinner, the modes, the locks, the bonus multiplier and the saver. */
  private syncProgressLamps(ctx: RulesContext, now: number): void {
    for (const shot of JACKPOT_ORDER) {
      const runLamp = this.run.shotLamp(shot);
      const ownLamp = this.shotLamp(shot, now);
      this.setLamp(ctx, SHOT_LIGHT[shot], runLamp > ownLamp ? runLamp : ownLamp);
    }
    this.setLamp(ctx, LIGHT.spinner, lamp(now < this.spinnerLitUntil));
    for (const id of TIMED_MODE_IDS)
      this.setLamp(ctx, MODE_LIGHTS[id] ?? "", lamp(this.modesDone.has(id)));
    this.setRow(ctx, LOCK_LIGHTS, (i) => this.lockCount > i);
    this.setRow(ctx, BONUS_LIGHTS, (i) => this.bonusMult >= i + 2);
    this.setLamp(ctx, LIGHT.saver, lamp(now < this.saverUntil));
  }

  /** Character animations that follow the state. */
  private syncAnimations(ctx: RulesContext): void {
    const lettersText = this.lanes.map((l) => (l ? "1" : "0")).join("");
    this.setAnim(ctx, DECORATION.larry, `letters:${lettersText}`);
    this.setAnim(ctx, DECORATION.carl, `fill:${this.tokensDown.filter(Boolean).length}`);
    this.setAnim(ctx, DECORATION.tessa, `step:${Math.min(10, this.rightOrbitsBall)}`);
    this.setAnim(ctx, DECORATION.prod, this.wizard ? `leds:${this.wizard.safe}` : "leds:off");
    this.setAnim(ctx, DECORATION.rex, `cmd:${this.rexCommand()}`);
    this.setAnim(ctx, HUD_TARGET, this.goalLine());
  }
}

/**
 * Create the rules of the SigmaBots table. Each call returns a new game state.
 * Pass `feedback` and `positionOf` to get feedback events. The rules play the same without them.
 */
export function createVibe01Rules(options: Vibe01Options = {}): TableRules {
  return new Vibe01Rules(options);
}
